import {
  MessageContentCategory,
  MessageContentType,
  SectionType,
  ThinkingRenderer,
  type MessageContent,
  type MessageSection,
} from '@rocketlane/rocket-llm';
import React, { useMemo } from 'react';

export type MeetingPageThinkingLogsProps = {
  /** Each completed thinking step’s `data.description` from the SSE stream. */
  descriptions: string[];
  /** While true, header uses the same shimmer “Thinking” state as chat. */
  isStreaming: boolean;
  /** Shown when thinking finished (e.g. “Thought for 8s”); logs stay expandable while content streams. */
  completedSummaryLabel?: string;
};

/**
 * Single chat-style thinking row (via {@link ThinkingRenderer} / rocket-llm).
 * Collapsed: “Thinking” with chevron when there is body text to expand.
 * Expanded: all descriptions, separated by blank lines.
 */
export const MeetingPageThinkingLogs: React.FC<
  MeetingPageThinkingLogsProps
> = ({ descriptions, isStreaming, completedSummaryLabel }) => {
  const section = useMemo((): MessageSection => {
    const body = descriptions.filter(Boolean).join('\n\n');

    const activityLabel = isStreaming
      ? 'Thinking'
      : (completedSummaryLabel ?? 'Thinking');

    const left_content: MessageContent = {
      type: MessageContentType.ACTIVITY_LOG,
      category: MessageContentCategory.VERBOSE,
      value: {
        activity_label: activityLabel,
        content: body,
        is_complete: !isStreaming,
      },
    };

    return {
      id: 'meeting-page-template-thinking',
      type: SectionType.THINKING_LOG,
      title: activityLabel,
      left_content,
    };
  }, [completedSummaryLabel, descriptions, isStreaming]);

  return <ThinkingRenderer.Component section={section} />;
};
